import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { TopBar } from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Lock, CheckCircle2 } from "lucide-react";

export function SetupCoach() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [token, setToken] = useState(params.get("token") || "");
  const [coachExists, setCoachExists] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/setup-status");
        setCoachExists(data.coach_exists);
        if (!data.coach_exists && !token) {
          try {
            const { data: t } = await api.get("/auth/active-setup-token");
            setToken(t.token);
          } catch { /* no active token */ }
        }
      } catch { setCoachExists(false); }
    })();
  }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (form.password !== form.confirm) { setErr("Passwords do not match"); return; }
    if (form.password.length < 12) { setErr("Password must be at least 12 characters"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/setup-coach", { setup_token: token, email: form.email, password: form.password, name: form.name });
      localStorage.setItem("kw_token", data.access_token);
      setUser(data);
      nav("/coach", { replace: true });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (coachExists) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar/>
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <Lock className="w-10 h-10 text-neutral-500 mx-auto"/>
        <h1 className="font-display font-black uppercase text-3xl mt-3">Coach already activated</h1>
        <p className="text-neutral-400 mt-2 text-sm">The coach account is set up. Use the login page.</p>
        <Link to="/login" className="kw-btn-primary inline-block mt-6" data-testid="setup-to-login">Go to login</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar showAuth={false} />
      <div className="max-w-md mx-auto px-5 py-12">
        <div className="flex items-center gap-2 text-gold">
          <ShieldCheck className="w-5 h-5"/>
          <span className="uppercase tracking-[0.3em] text-xs">Private setup</span>
        </div>
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter mt-2">Activate your Coach account</h1>
        <p className="text-neutral-400 mt-2 text-sm">One-time secure activation. Only you — the business owner — will know this email and password.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="kw-card p-3">
            <div className="text-[10px] uppercase tracking-widest text-gold">Activation token</div>
            <div className="font-mono text-xs text-neutral-400 break-all mt-1" data-testid="setup-token-display">{token ? "✓ Loaded — ready to activate" : "No active token found. Restart the backend."}</div>
            <input type="hidden" value={token} data-testid="setup-token" readOnly/>
          </div>
          <div><label className="kw-label">Your name</label><input className="kw-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required data-testid="setup-name"/></div>
          <div><label className="kw-label">Your coach email</label><input className="kw-input" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required data-testid="setup-email"/></div>
          <div><label className="kw-label">Password (min 12 chars)</label><input className="kw-input" type="password" minLength={12} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required data-testid="setup-password"/></div>
          <div><label className="kw-label">Confirm password</label><input className="kw-input" type="password" value={form.confirm} onChange={e=>setForm({...form,confirm:e.target.value})} required data-testid="setup-confirm"/></div>
          {err && <div className="text-red-400 text-sm" data-testid="setup-error">{err}</div>}
          <button className="kw-btn-primary w-full" disabled={loading} data-testid="setup-submit">{loading? "Activating…":"Activate Coach account"}</button>
          <p className="text-[11px] text-neutral-500">Your password is hashed with bcrypt and stored encrypted. It is never shown anywhere.</p>
        </form>
      </div>
    </div>
  );
}

export function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ password: "", confirm: "", name: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/${token}`);
        setInvite(data);
        setForm(f => ({ ...f, name: data.name || "" }));
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (form.password !== form.confirm) { setErr("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { invite_token: token, email: invite.email, password: form.password, name: form.name });
      localStorage.setItem("kw_token", data.access_token);
      setUser(data);
      nav("/onboarding", { replace: true });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (err && !invite) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar/>
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <Lock className="w-10 h-10 text-neutral-500 mx-auto"/>
        <h1 className="font-display font-black uppercase text-3xl mt-3">Invite unavailable</h1>
        <p className="text-neutral-400 mt-2 text-sm">{err}</p>
        <Link to="/apply" className="kw-btn-primary inline-block mt-6">Apply for coaching</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar showAuth={false}/>
      <div className="max-w-md mx-auto px-5 py-12">
        <div className="flex items-center gap-2 text-gold"><CheckCircle2 className="w-5 h-5"/><span className="uppercase tracking-[0.3em] text-xs">You're in</span></div>
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter mt-2">Create your warrior account</h1>
        {invite && <p className="text-neutral-400 mt-2 text-sm">Invite for <span className="text-gold">{invite.email}</span></p>}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div><label className="kw-label">Your name</label><input className="kw-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required data-testid="inv-name"/></div>
          <div><label className="kw-label">Password (min 8 chars)</label><input className="kw-input" type="password" minLength={8} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required data-testid="inv-password"/></div>
          <div><label className="kw-label">Confirm password</label><input className="kw-input" type="password" value={form.confirm} onChange={e=>setForm({...form,confirm:e.target.value})} required data-testid="inv-confirm"/></div>
          {err && <div className="text-red-400 text-sm">{err}</div>}
          <button className="kw-btn-primary w-full" disabled={loading} data-testid="inv-submit">{loading? "Creating…":"Create account & start onboarding"}</button>
        </form>
      </div>
    </div>
  );
}

export function ChangePassword() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [err, setErr] = useState(""); const [ok, setOk] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setOk(false);
    if (form.new_password !== form.confirm) { setErr("Passwords do not match"); return; }
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      setOk(true); setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { setErr(formatApiError(e)); }
  };
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar/>
      <div className="max-w-md mx-auto px-5 py-12">
        <h1 className="font-display font-black uppercase text-3xl tracking-tighter">Change password</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div><label className="kw-label">Current password</label><input type="password" className="kw-input" value={form.current_password} onChange={e=>setForm({...form,current_password:e.target.value})} required data-testid="cp-cur"/></div>
          <div><label className="kw-label">New password (min 12)</label><input type="password" minLength={12} className="kw-input" value={form.new_password} onChange={e=>setForm({...form,new_password:e.target.value})} required data-testid="cp-new"/></div>
          <div><label className="kw-label">Confirm</label><input type="password" className="kw-input" value={form.confirm} onChange={e=>setForm({...form,confirm:e.target.value})} required data-testid="cp-conf"/></div>
          {err && <div className="text-red-400 text-sm">{err}</div>}
          {ok && <div className="text-gold text-sm" data-testid="cp-ok">Password updated.</div>}
          <button className="kw-btn-primary w-full" data-testid="cp-submit">Update password</button>
        </form>
      </div>
    </div>
  );
}
